import { types } from "mobx-state-tree";

import { MenuItems } from "../../components/secondary-menu/SecondaryMenu";
import {
  CreateProject,
  ViewProject,
  EditProject,
  ViewProjectAssignedTo,
  ViewProjectTeamMembers,
  ViewJobsFromAssignedProject,
  ViewJobsList,
  ViewSewerObjectsFromAssignedProject,
  ViewSewerObjects,
  ViewTeamMembersFromAssignedProject,
  EditProjectAssignedTo,
  ViewInspectionsList,
} from "../../services/authorization/Permissions";
import { permissionsService } from "../../services/authorization/UserPermissionsService";

export enum ProjectDynamicMenuItemsPages {
  EditProject,
}

export const ProjectMenuItemsModel = types
  .model({
    projectId: types.optional(types.number, 0),
    isLoading: false,
    editMode: types.optional(types.boolean, false),
    createMode: types.optional(types.boolean, false),
    dynamicMenuItemPage: types.maybe(types.frozen<ProjectDynamicMenuItemsPages>()),
    numberOfSewerObjects: types.optional(types.number, 0),
  })
  .actions((self) => {
    const getProjectDetailsPath = () =>
      self.createMode ? "/create-project" : `/${self.projectId}`;

    const canAccessProjectDetailsPage = () =>
      self.createMode
        ? permissionsService.userHasPermissionTo(CreateProject)
        : self.editMode
        ? permissionsService.userHasOneOfThePermissions([EditProject, EditProjectAssignedTo])
        : permissionsService.userHasOneOfThePermissions([ViewProject, ViewProjectAssignedTo]);

    const canAccessProjectSewerObjectsPage = () =>
      self.createMode ||
      permissionsService.userHasOneOfThePermissions([
        ViewSewerObjects,
        ViewSewerObjectsFromAssignedProject,
      ]);

    const canAccessProjectSewerObjectsMapPage = () =>
      permissionsService.userHasOneOfThePermissions([
        ViewSewerObjects,
        ViewSewerObjectsFromAssignedProject,
      ]);

    const canAccessProjectJobsPage = () =>
      self.createMode ||
      permissionsService.userHasOneOfThePermissions([ViewJobsList, ViewJobsFromAssignedProject]);

    const canAccessProjectMembersPage = () =>
      self.createMode ||
      permissionsService.userHasOneOfThePermissions([
        ViewProjectTeamMembers,
        ViewTeamMembersFromAssignedProject,
      ]);

    const canAccessProjectInspectionsPage = () =>
      self.createMode ||
      permissionsService.userHasOneOfThePermissions([
        ViewInspectionsList
      ]) &&
      permissionsService.userHasOneOfThePermissions([
        ViewSewerObjects,
        ViewSewerObjectsFromAssignedProject
      ]);

    const isProjectItemPageDisabled = () => self.createMode || self.isLoading;

    return {
      getStaticItems(): MenuItems {
        return {
          "general.general": {
            items: [
              {
                id: "secondary-menu-projects-details",
                labelKey: "general.projects.details",
                path: getProjectDetailsPath(),
                canAccess: canAccessProjectDetailsPage(),
                disabled: self.isLoading,
                isActive: self.dynamicMenuItemPage === ProjectDynamicMenuItemsPages.EditProject,
              },
            ],
            defaultOpen: true,
          },
          "general.manage": {
            items: [
              {
                id: "secondary-menu-sewer-objects",
                labelKey: "general.sewer-objects",
                path: `/${self.projectId}/sewer-objects`,
                canAccess: canAccessProjectSewerObjectsPage(),
                disabled: isProjectItemPageDisabled(),
              },
              {
                id: "secondary-menu-projects-inspections",
                labelKey: "general.inspections",
                path: `/${self.projectId}/inspections`,
                canAccess: canAccessProjectInspectionsPage(),
                disabled: isProjectItemPageDisabled()
              },
              {
                id: "secondary-menu-map",
                labelKey: "general.sewer-objects.map",
                path: `/${self.projectId}/map`,
                canAccess: canAccessProjectSewerObjectsMapPage(),
                disabled: isProjectItemPageDisabled(),
              },
              {
                id: "secondary-menu-jobs",
                labelKey: "general.jobs",
                path: `/${self.projectId}/jobs`,
                canAccess: canAccessProjectJobsPage(),
                disabled: isProjectItemPageDisabled(),
              },
              {
                id: "secondary-menu-projects-members",
                labelKey: "general.projects.members",
                path: `/${self.projectId}/members`,
                canAccess: canAccessProjectMembersPage(),
                disabled: isProjectItemPageDisabled(),
              }
            ],
            defaultOpen: true,
          },
        };
      },
    };
  });
